export const IST_TIME_ZONE = "Asia/Kolkata";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (value) => String(value).padStart(2, "0");

const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

const getDatePartsInTimeZone = (date, timeZone = IST_TIME_ZONE) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  return {
    year: parts.find((part) => part.type === "year")?.value,
    month: parts.find((part) => part.type === "month")?.value,
    day: parts.find((part) => part.type === "day")?.value,
  };
};

const toDateInstance = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return isValidDate(value) ? value : null;
  }

  if (typeof value === "string") {
    const dateOnlyMatch = value.match(DATE_ONLY_PATTERN);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }

  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : null;
};

export const toApiDate = (value) => {
  const date = toDateInstance(value);
  if (!date) return "";

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const parseApiDate = (value) => toDateInstance(value);

export const normalizeApiDateString = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const dateOnlyMatch = value.match(DATE_ONLY_PATTERN);
    if (dateOnlyMatch) {
      return value;
    }
  }

  const date = toDateInstance(value);
  if (!date) return "";

  const { year, month, day } = getDatePartsInTimeZone(date);
  return `${year}-${month}-${day}`;
};

export const formatDateOnlyIST = (value, fallback = "NA") => {
  if (!value) return fallback;

  if (typeof value === "string") {
    const dateOnlyMatch = value.match(DATE_ONLY_PATTERN);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return `${day}/${month}/${year}`;
    }
  }

  const date = toDateInstance(value);
  if (!date) return fallback;

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

export const formatDateTimeIST = (value, fallback = "NA") => {
  const date = toDateInstance(value);
  if (!date) return fallback;

  const formatted = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);

  return `${formatted}`;
};

export const formatFileSize = (value, fallback = "N/A") => {
  const sizeInBytes = Number(value);
  if (!Number.isFinite(sizeInBytes) || sizeInBytes < 0) {
    return fallback;
  }

  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  }

  return `${(sizeInBytes / 1024).toFixed(2)} KB`;
};
